// SETTLE LATER — batch 3's rebuild of Settlements (UI rebuild; drawn when lib/ui-v2.ts switches batch 3 on).
//
// THE MODEL IS TODAY'S (operator, 26 Oct 2026, via the Command Centre: "keep today's per-party model and posting, in
// Astra's lighter look"; verification 2.1). The unit is the PARTY BOX, not the deal (Settlements.tsx:6–17): a
// settle-later trade leaves its two legs in the party's box and settling is ordinary money in / out against it, which
// the sweep matches leg by leg. So each group is a party with its box in the Books' words (owes you / you hold /
// square), and its open trades beneath it, each open leg with its OPEN figure, whose side it is on, and its door —
// Record money prefilled with the party, the leg's asset, the open figure and the way it moves (components/v2/settle/
// parts.tsx settleHref; the params RecordMoney reads today). No deal-scoped form, no new posting.
//
// THE READ IS SETTLEMENTS' (the same key, the same live options: refetch on focus, on mount and every 15 s — a receipt
// posted elsewhere shows here without a navigation; reading it runs the sweep). What changed is the drawing (Astra,
// settle): PageHead with Book trade the one gold primary; search first; ONE register — a table from lg, a card per
// party on the phone; a trade opens in the kit's DetailSheet (?deal= in the URL, so Back closes it) where Mark settled
// (the guarded override), Hold leg and the party's box live. The trades this read closed — with what RODE on a running
// account — stay one quiet panel above the list: it is the only time he is told (verification 3.2).
// THE READS (Appendix B, through readState): a first read waits (SkeletonRows), a failed first read is a Notice with Try
// again, a failed refresh keeps its rows under one Notice and claims nothing from them; "Nothing awaiting" needs a
// successful read.
import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Plus } from 'lucide-react';
import { api, type SettlementParty } from '../../lib/api';
import { readState } from '../../lib/read-state';
import { displayAsset, fmtAmount, fmtStamp } from '../../lib/format';
import { dustFor, partyName, shortReference } from '../../lib/naming';
import { legWords } from '../../components/SettleButton';
import { BoxWords } from '../../components/BoxLines';
import { Amount } from '../../components/Ledger';
import { LAST_READ_NONE, ReadFailed } from '../../components/OnboardingParts';
import { EmptyState, Notice, PageHead, Panel, Pending, SearchBar, SkeletonRows, ReadNotice, WhenGutter } from '../../components/v2/kit';
import { SettleDoor, SweptPanel, openLegsOf, sideWords } from '../../components/v2/settle/parts';
import { SettleDetail } from '../../components/v2/settle/SettleDetail';
import { MarkSettled } from '../../components/v2/settle/MarkSettled';

type Deal = SettlementParty['deals'][number];

/** The laptop's seats: when · party / agreement · remaining · direction · next step · the chevron. WHEN IS ITS OWN SEAT (his
 *  "date and time everything in 1st column", 1 Nov 2026), as on every register. */
// The table from 1024 (a laptop keeps its table): When first — the day over the time at 1024, one line from 1280 — then the
// agreement, which keeps about 150 px at 1024; the cards below 1024.
const COLS = 'lg:grid lg:grid-cols-[4.5rem_minmax(0,1fr)_150px_130px_120px_16px] lg:gap-x-3 xl:grid-cols-[8.5rem_minmax(0,1fr)_220px_160px_150px_16px] xl:gap-x-4';

/** The second line under a trade: any leg already closed ("USDT received", "INR on account · 15,705 rode") — Astra's "D-1040 ·
 *  Part settled" in the Books' words; the phone's card (`card`), which has no When seat, leads it with when it was agreed. */
function metaOf(d: Deal): string {
  const done = d.legs ? (['take', 'give'] as const).filter((s) => d.legs![s].settled).map((s) => legWords(d.legs![s], s)) : [];
  return done.join(' · ');
}

/** The When seat's stamp, as the kit's When column draws it: the day over the time at 1024, one line from 1280. */
function StampCell({ at }: { at: string }) {
  const stamp = fmtStamp(at);
  const [day, time] = stamp.includes(', ') ? stamp.split(', ') : ['', stamp];
  return (
    <>
      {day && <span className="block whitespace-nowrap text-meta text-tg-hint xl:inline xl:text-body xl:text-tg-text">{day}<span className="hidden xl:inline">, </span></span>}
      <span className="whitespace-nowrap">{time}</span>
    </>
  );
}

/** Does a search find this trade? The party (shown and stored name, slug), the reference, and the figures — typed with
 *  or without the thousands commas. */
function matches(p: SettlementParty, d: Deal | null, needle: string): boolean {
  const parts = [partyName({ display_name: p.display_name, slug: p.slug }), p.display_name ?? '', p.slug];
  if (d) {
    parts.push(d.deal_id, shortReference(d.deal_id), String(d.amount_take), String(d.amount_give),
      fmtAmount(d.amount_take, d.asset_take), fmtAmount(d.amount_give, d.asset_give));
    for (const { leg } of openLegsOf(d.legs)) parts.push(String(leg.open), fmtAmount(leg.open, leg.asset));
  }
  const hay = parts.join(' ').toLowerCase();
  const bare = needle.replace(/,/g, '');
  return hay.includes(needle) || (bare !== '' && hay.replace(/,/g, '').includes(bare));
}

/** What the open legs of a party's trades add up to, per asset — owed to the desk positive, owed by it negative. */
function legsOpenOf(p: SettlementParty): Map<string, number> {
  const m = new Map<string, number>();
  for (const d of p.deals) for (const { side, leg } of openLegsOf(d.legs)) m.set(leg.asset, (m.get(leg.asset) ?? 0) + (side === 'take' ? leg.open : -leg.open));
  return m;
}

/** THE BOX, ONLY WHERE THE TRADES DO NOT ALREADY SAY IT (Astra, 1 Nov 2026: one amount, one button): an asset whose box differs
 *  from what its open legs add up to — money on account, a part payment — with its door for the WHOLE box, Record money
 *  prefilled. Each its own row (a 44 px target on the phone, none overlapping). */
function BoxLine({ p }: { p: SettlementParty }) {
  const legs = legsOpenOf(p);
  const box = Object.entries(p.box).filter(([a, v]) => Math.abs(v) > dustFor(a) && Math.abs(v - (legs.get(a) ?? 0)) > dustFor(a));
  if (box.length === 0) return null;
  const href = (a: string, v: number) =>
    `/record?party=${encodeURIComponent(p.slug)}&dir=${v > 0 ? 'in' : 'out'}&asset=${encodeURIComponent(a)}&amount=${Math.abs(v)}`;
  return (
    <span className="flex flex-col gap-x-4 text-body lg:flex-row lg:flex-wrap">
      {box.map(([a, v]) => (
        <span key={a} className="flex min-h-11 items-center gap-x-2 whitespace-nowrap lg:min-h-0">
          <span className="text-tg-hint">{v > 0 ? 'Owes you' : 'You hold'}</span>
          <span className={`whitespace-nowrap ${v < 0 ? 'text-neg' : 'text-tg-text'}`}><span className="amt">{fmtAmount(Math.abs(v), a)}</span> <span className="text-tg-hint">{displayAsset(a)}</span></span>
          {/* THE SAME DOOR as a trade's leg (Astra's audit #7, 1 Nov 2026: a small link here, a filled button on every row). */}
          <Link to={href(a, v)} className={`inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-lg px-3 text-body font-medium hover:opacity-90 lg:min-h-10 ${v > 0 ? 'bg-info-bg text-info' : 'bg-warn-bg text-warn'}`}
            title={`${v > 0 ? 'Receive' : 'Pay out'} ${fmtAmount(Math.abs(v), a)} ${displayAsset(a)}`}>{v > 0 ? 'Receive' : 'Pay out'}</Link>
        </span>
      ))}
    </span>
  );
}

/** One open trade: its reference and when (the agreement sentence is in the trade's detail), then ONE ROW PER OPEN LEG —
 *  the figure still open · whose side · its door, on the same seats — and Mark settled on a row of its own under them
 *  (Astra, 1 Nov 2026). The whole laptop row opens the trade; the doors are their own links. */
function TradeRow({ p, d, first, onOpen }: { p: SettlementParty; d: Deal; first: boolean; onOpen: (d: Deal) => void }) {
  const legs = openLegsOf(d.legs);
  // The time is never on this line: the table has its When seat, the card its time gutter.
  const title = (card: boolean) => {
    const meta = metaOf(d);
    return (
      <button type="button" onClick={() => onOpen(d)} className="tap-44 block w-full min-w-0 text-left">
        <span className="block font-mono text-body hover:underline">{shortReference(d.deal_id)}</span>
        {meta && <span className="mt-1 block break-words text-meta text-tg-hint">{meta}</span>}
      </button>
    );
  };
  const chevron = <ChevronRight className="h-4 w-4 text-tg-hint" aria-hidden />;
  return (
    <div className={first ? '' : 'border-t border-stroke-soft'}>
      {/* the laptop: the legs on one grid each, the title and the chevron on the first */}
      <div className={`hidden cursor-pointer px-5 hover:bg-panel-2 lg:block ${first ? 'pb-3 pt-1' : 'py-3'}`}
        onClick={(e) => { if (!(e.target as HTMLElement).closest('a,button')) onOpen(d); }}>
        {(legs.length > 0 ? legs : [null]).map((l, i) => (
          <div key={l?.side ?? 'none'} className={`${COLS} items-center ${i > 0 ? 'mt-2' : ''}`}>
            <div className="font-mono tabular text-body">{i === 0 && <StampCell at={d.booked_at} />}</div>
            <div className="min-w-0">{i === 0 && title(false)}</div>
            <div className="text-right text-body">{l ? <Amount value={l.leg.open} asset={l.leg.asset} /> : <span className="text-tg-hint">—</span>}</div>
            <div className="text-right text-body">{l && sideWords(l.side)}</div>
            <div className="flex min-h-10 items-center justify-end">{l && l.leg.open > 0 && <SettleDoor partySlug={p.slug} side={l.side} leg={l.leg} />}</div>
            <div>{i === 0 && chevron}</div>
          </div>
        ))}
        {/* Mark settled 12 px under the door above it — two actions, never one joined block (Astra, 1 Nov 2026). */}
        <div className={`mt-3 ${COLS}`}>
          <span /><span /><span /><span />
          <div className="flex justify-end"><MarkSettled dealId={d.deal_id} partySlug={p.slug} quiet /></div>
          <span />
        </div>
      </div>
      {/* the phone: the trade and its chevron, then each leg — figure and side, its door — then Mark settled */}
      {/* the card: its time in the kit's left gutter (as every register's card), the trade and its legs beside it */}
      <div className={`px-4 sm:flex sm:items-start sm:gap-3 lg:hidden ${first ? 'pb-4 pt-1' : 'py-4'}`}>
        <WhenGutter at={d.booked_at} />
        <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">{title(true)}</div>
          <span className="mt-0.5 shrink-0">{chevron}</span>
        </div>
        {legs.map(({ side, leg }) => (
          <div key={side} className="mt-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-body"><Amount value={leg.open} asset={leg.asset} /></div>
              <div className="mt-1 text-meta text-tg-hint">{sideWords(side)}</div>
            </div>
            {leg.open > 0 && <SettleDoor partySlug={p.slug} side={side} leg={leg} />}
          </div>
        ))}
        {/* Mark settled is in the trade's sheet on the phone: under the payment door its tap box would overlap it. */}
        </div>
      </div>
    </div>
  );
}

/** A party: its name (to its page) and its box, then its open trades. */
function PartyGroup({ p, deals, onOpen }: { p: SettlementParty; deals: Deal[]; onOpen: (d: Deal) => void }) {
  const name = partyName({ display_name: p.display_name, slug: p.slug });
  return (
    <section aria-label={name} className="border-b border-stroke last:border-b-0">
      {/* On the phone the name is a 44 px row of its own (the trade's button sits right under it: an invisible tap box
          round the words would land on the trade); the head's padding gives back most of what it takes. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 pb-0 pt-2 lg:items-baseline lg:px-5 lg:pb-2 lg:pt-4">
        <Link to={`/parties/${encodeURIComponent(p.slug)}?entity=dubaiotc`} className="inline-flex min-h-11 min-w-0 items-center break-words text-body font-medium hover:underline lg:inline lg:min-h-0">{name}</Link>
        <BoxLine p={p} />
      </div>
      {deals.map((d, i) => <TradeRow key={d.deal_id} p={p} d={d} first={i === 0} onOpen={onOpen} />)}
    </section>
  );
}

export function SettleV2() {
  // LIVE (operator, 3 Sept: "screen not updating instantly") — Settlements' read, word for word.
  const q = useQuery({ queryKey: ['settlements'], queryFn: () => api.settlements(), staleTime: 0,
    refetchOnWindowFocus: true, refetchOnMount: 'always', refetchInterval: 15_000 });
  const rs = readState(q);
  const parties = q.data?.parties ?? [];
  const swept = q.data?.swept ?? [];

  const [params, setParams] = useSearchParams();
  // ?q= — a party page's "Settle later →" lands on its own trades.
  const [search, setSearch] = useState(() => params.get('q') ?? '');
  const needle = search.trim().toLowerCase();

  const shown = useMemo(() => parties
    .map((p) => ({ p, deals: !needle || matches(p, null, needle) ? p.deals : p.deals.filter((d) => matches(p, d, needle)) }))
    .filter((g) => g.deals.length > 0 || (!needle && g.p.deals.length === 0)), [parties, needle]);
  const trades = shown.reduce((n, g) => n + g.deals.length, 0);

  // THE OPEN TRADE lives in the URL: Back closes it. A trade the sweep closes meanwhile leaves the read, and the sheet
  // with it — its line is then in the panel above.
  const openId = params.get('deal');
  const selected = useMemo(() => {
    for (const p of parties) { const d = p.deals.find((x) => x.deal_id === openId); if (d) return { p, d }; }
    return null;
  }, [parties, openId]);
  const open = useCallback((d: Deal) => setParams((x) => { const n = new URLSearchParams(x); n.set('deal', d.deal_id); return n; }), [setParams]);
  const close = useCallback(() => setParams((x) => { const n = new URLSearchParams(x); n.delete('deal'); return n; }, { replace: true }), [setParams]);

  const head = (
    <PageHead
      title="Settle later"
      inline
      actions={<Link to="/trade" className="btn-primary px-4 text-body"><Plus className="h-4 w-4" aria-hidden />Book trade</Link>}
    />
  );

  // A first read on its way — paused offline too: "reading", never "nothing awaiting".
  if (rs === 'waiting' && !q.data) {
    return (
      <>
        {head}
        <Panel flush>
          <div className="px-4 pt-4 text-body lg:px-5"><Pending what="Reading settle-later trades…" /></div>
          <SkeletonRows what="settle-later trades" rows={4} />
        </Panel>
      </>
    );
  }
  // A failed first read: one Notice and its Try again, nothing drawn under it.
  if (rs === 'failed' && !q.data) {
    return (
      <>
        {head}
        <ReadNotice what="settle-later trades" error={q.error} onRetry={() => { void q.refetch(); }} />
      </>
    );
  }

  const known = rs === 'known';
  return (
    <>
      {head}
      {/* A failed refresh keeps the rows it had and says so, once; nothing below is counted from them. */}
      {rs === 'failed' && (
        <Notice tone="warn">
          <ReadFailed what="settle-later trades" error={q.error} says={parties.length === 0 ? LAST_READ_NONE : 'showing the last good read'} retry={() => { void q.refetch(); }} />
        </Notice>
      )}

      <SweptPanel swept={swept} />

      <SearchBar value={search} onChange={setSearch} placeholder="Search name, reference or amount" />

      <Panel flush>
        {shown.length > 0 && (
          <div className={`hidden border-b border-stroke bg-panel-2 px-5 py-3 text-label font-medium uppercase tracking-wider text-tg-hint ${COLS}`}>
            <span>When</span>
            <span>Party / agreement</span>
            <span className="text-right">Remaining</span>
            <span className="text-right">Direction</span>
            <span className="text-right">Next step</span>
            <span />
          </div>
        )}
        {shown.map(({ p, deals }) => <PartyGroup key={p.slug} p={p} deals={deals} onOpen={open} />)}

        {/* AN EMPTY CLAIM NEEDS A SUCCESSFUL READ. */}
        {shown.length === 0 && known && (
          <EmptyState className="px-4 lg:py-16" action={needle ? <button type="button" className="btn-link" onClick={() => setSearch('')}>Clear search</button> : undefined}>
            {needle ? 'Nothing matches this search.' : 'Nothing awaiting.'}
          </EmptyState>
        )}

        {/* ASTRA'S FOOT: the count only from a successful read; the order is the books' (oldest first). */}
        {shown.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
            <span className="min-w-0">
              {[known && `${shown.length} ${shown.length === 1 ? 'party' : 'parties'} · ${trades} ${trades === 1 ? 'trade' : 'trades'}`, 'Oldest first'].filter(Boolean).join(' · ')}
            </span>
            <span>Dubai time</span>
          </div>
        )}
      </Panel>

      {selected && <SettleDetail key={selected.d.deal_id} party={selected.p} deal={selected.d} onClose={close} />}
    </>
  );
}
